/**
 * Buy-side import markup (Phase Wellspring W1).
 *
 * Raw distributor price sheets carry a BASE price that excludes the distributor's
 * markup. The adapter converts base -> ISG's real cost by applying a markup % at
 * ingestion and baking it into cost_usd. This is NOT a profitability-engine factor
 * (the engine reads the already-marked-up cost_usd and must NEVER re-apply it —
 * double-count); the markup default lives on the manufacturer<->distributor
 * junction (manufacturer_distributors.markup_pct), the discount_pct sibling
 * (ADR 48). The junction fetch lives in the import hook; these helpers are pure.
 */

/**
 * Resolve the markup % by precedence: per-file override > junction default > 0.
 * `junctionMarkupPct` is manufacturer_distributors.markup_pct for the
 * (ingestion-manufacturer, distributor) pair, or null when no row exists.
 */
export function resolveMarkupPct(
  perFileOverride: number | null | undefined,
  junctionMarkupPct: number | null | undefined
): number {
  if (perFileOverride != null && Number.isFinite(perFileOverride)) return perFileOverride;
  if (junctionMarkupPct != null && Number.isFinite(junctionMarkupPct)) return junctionMarkupPct;
  return 0;
}

/**
 * Apply a markup % to a base cost, float-safe cent-rounded:
 *   cost_usd = round(base * (1 + markupPct/100), 2)
 * Mirrors the engine's discount form (* (1 - discount_pct/100)); rounds to cents
 * because cost_usd is a persisted, unconstrained-numeric money value (no DB round).
 */
export function applyMarkup(base: number, markupPct: number): number {
  return Math.round(base * (1 + markupPct / 100) * 100) / 100;
}

/** The import's sheet modes that decide whether a markup applies at all. */
export type ImportSheetMode = 'price_sheet' | 'supplier_sheet';

/** The settings an import ran under, as the hook resolves them once per file. */
export interface ImportMarkupSettings {
  mode: ImportSheetMode;
  /** The per-file override typed in the import modal, or null. */
  perFileOverridePct: number | null | undefined;
  /** manufacturer_distributors.markup_pct for the (ingestion-manufacturer, distributor) pair, or null. */
  manufacturerMarkupPct: number | null | undefined;
}

/**
 * The markup % an import applies, resolved once per file:
 *   supplier_sheet (clearance): the typed clearance price IS the cost — never marked up → 0.
 *   price_sheet: per-file override > manufacturer default > 0 (resolveMarkupPct).
 * Stamped on ingestion_batches.markup_pct and used for every row of the file.
 */
export function resolveImportMarkupPct(settings: ImportMarkupSettings): number {
  if (settings.mode === 'supplier_sheet') return 0;
  return resolveMarkupPct(
    settings.perFileOverridePct ?? null,
    settings.manufacturerMarkupPct ?? null
  );
}

/**
 * The unit cost the import stores for one sheet row: the row's base cost with the file's
 * resolved markup baked in, rounded once to the cent by applyMarkup. At 0 % the base is
 * stored as parsed, unrounded. This is the one place the three rules live; the import hook
 * and the answer-key runner (scripts/answer-key-run.ts) both call it.
 */
export function importUnitCost(base: number, settings: ImportMarkupSettings): number {
  const pct = resolveImportMarkupPct(settings);
  return pct > 0 ? applyMarkup(base, pct) : base;
}
